import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Calendar, 
  FileText, 
  Link as LinkIcon,
  Upload, 
  Briefcase
} from 'lucide-react';
import styles from './NewSubProjectManagerV2.module.scss';
import { 
  NewSubProject, 
  NewProjectValidationState 
} from '../../../types/newProject';
import { newValidationService } from '../../../services/newValidationService.ts';

interface NewSubProjectManagerV2Props {
  subProjects: NewSubProject[];
  validation: NewProjectValidationState;
  onAddSubProject: (subProject: NewSubProject) => void;
  onUpdateSubProject: (subProjectId: string, data: Partial<NewSubProject>) => void;
  onRemoveSubProject: (subProjectId: string) => void;
  onOpenSubProjectModal: (subProjectId?: string) => void;
  isEditing?: boolean;
  isLoading?: boolean;
  projectStartDate?: string;
  projectEndDate?: string;
}

const NewSubProjectManagerV2: React.FC<NewSubProjectManagerV2Props> = ({
  subProjects,
  validation,
  onAddSubProject,
  onUpdateSubProject,
  onRemoveSubProject,
  onOpenSubProjectModal,
  isEditing = false,
  isLoading = false,
  projectStartDate,
  projectEndDate
}) => {

  // Crear nuevo subproyecto
  const handleAddSubProject = useCallback(() => {
    // Solo abrir el modal sin crear el subproyecto aún
    // El subproyecto se creará cuando se guarde desde el modal
    onOpenSubProjectModal('new');
  }, [onOpenSubProjectModal]);

  // Eliminar subproyecto
  const handleRemoveSubProject = useCallback((subProjectId: string) => {
    onRemoveSubProject(subProjectId);
  }, [onRemoveSubProject]);

  // Editar subproyecto
  const handleEditSubProject = useCallback((subProjectId: string) => {
    onOpenSubProjectModal(subProjectId);
  }, [onOpenSubProjectModal]);


  // Validar subproyecto
  const isSubProjectValid = useCallback((subProject: NewSubProject): boolean => {
    return newValidationService.validateSubProjectData(subProject);
  }, []);

  // Obtener errores de validación para un subproyecto
  const getSubProjectErrors = useCallback((subProjectId: string) => {
    return validation.subProjects[subProjectId] || {};
  }, [validation.subProjects]);

  // Formatear fecha
  const formatDate = useCallback((date: string): string => {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  // Obtener estado del subproyecto
  const getSubProjectStatus = useCallback((subProject: NewSubProject) => {
    if (subProject.isNew) return 'new';
    if (subProject.isEdited) return 'edited';
    return 'existing';
  }, []);

  return (
    <div className={styles.subProjectManager}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>
            <Briefcase size={20} className={styles.briefcaseIcon}/>
            Rubros a cotizar *
          </h3>
          <p className={styles.subtitle}>
            Agrega los trabajos específicos que necesitas cotizar
          </p>
        </div>
        
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAddSubProject}
          disabled={isLoading}
        >
          <Plus size={20} />
          Agregar rubro
        </button>
      </div>

      {/* Lista de subproyectos */}
      <div className={styles.subProjectsList}>
        {subProjects.length === 0 ? (
          <motion.div
            key="empty-state"
            className={styles.emptyState}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className={styles.emptyStateIcon}>
              <FileText size={48} />
            </div>
            <h4 className={styles.emptyStateTitle}>No hay rubros a cotizar</h4>
            <p className={styles.emptyStateText}>
              Agrega al menos un rubro para continuar
            </p>
          </motion.div>
        ) : (
            <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.5 }}
            className={styles.subProjectsList}
          >
            {subProjects.map((subProject, index) => {
              const errors = getSubProjectErrors(subProject.id || index.toString());
              const isValid = isSubProjectValid(subProject);
              const status = getSubProjectStatus(subProject);
              
              return (
                <motion.div
                  key={subProject.id}
                  className={`${styles.subProjectItem} ${styles[status]} ${!isValid ? styles.invalid : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >

                  {/* Contenido del subproyecto */}
                  <div className={styles.subProjectContent}>
                    <div className={styles.subProjectHeader}>
                      <h4 className={styles.subProjectName}>
                        {subProject.name || 'Rubro sin nombre'}
                      </h4>
                      <div className={styles.subProjectActions}>
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => handleEditSubProject(subProject.id!)}
                          disabled={isLoading}
                          title="Editar rubro"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => handleRemoveSubProject(subProject.id!)}
                          disabled={isLoading}
                          title="Eliminar rubro"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.subProjectDetails}>
                      <p className={styles.subProjectDescription}>
                        {subProject.description || 'Sin descripción'}
                      </p>
                      
                      <div className={styles.subProjectMeta}>
                        <div className={styles.metaItem}>
                          <Calendar size={14} />
                          <span>
                            {formatDate(subProject.startDate)} - {formatDate(subProject.endDate)}
                          </span>
                        </div>
                        
                        {subProject.link && (
                          <div className={styles.metaItem}>
                            <LinkIcon size={14} />
                            <span>Con enlace</span>
                          </div>
                        )}
                        
                        {(subProject.plantillaDePresupuesto || subProject.planosArchivo) && (
                          <div className={styles.metaItem}>
                            <Upload size={14} />
                            <span>Con archivos</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Errores de validación */}
                    {Object.keys(errors).length > 0 && (
                      <div className={styles.validationErrors}>
                        {Object.values(errors).map((error, errorIndex) => (
                          <span key={errorIndex} className={styles.errorText}>
                            {error}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Indicador de estado */}
                  <div className={`${styles.statusIndicator} ${styles[status]}`}>
                    {status === 'new' && 'Nuevo'}
                    {status === 'edited' && 'Editado'}
                    {status === 'existing' && 'Existente'}
                  </div>
                </motion.div>
              );
            })
            }
          </motion.div>
        )}
      </div>

      {/* Footer con información */}
      <div className={styles.footer}>
        <div className={styles.footerInfo}>
          <span className={styles.subProjectCount}>
            {subProjects.length} rubro{subProjects.length !== 1 ? 's' : ''}
          </span>
          {subProjects.length > 0 && (
            <span className={styles.validCount}>
              {subProjects.filter(isSubProjectValid).length} válido{subProjects.filter(isSubProjectValid).length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        {validation.global.hasSubProjects && (
          <div className={styles.globalError}>
            {validation.global.hasSubProjects}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewSubProjectManagerV2;
